import { Button, Chip, DismissControl } from '@patchbay/ui'
import { Check, Zap } from 'lucide-react'
import * as React from 'react'
import { acknowledgementOf } from './card'
import { isReplyCard, type RileyCard } from './contracts'

/**
 * Card kinds that only touch Ewee's own records, so "Confirm all" may take them in one click (#417). A customer or
 * contractor send, a close, a dispatch, money or a bulk change is never in this set: those are decided one by one.
 */
export const LOW_RISK_KINDS: ReadonlySet<string> = new Set([
  'set_next_move',
  'notify_teammate',
  'redraft_comment',
  'post_note',
  'conduit_note',
  'retriage',
  'hygiene_fix',
])

/** Low-risk, internal-only and ready to run as is (no customer send, nothing blocking it). */
export function isLowRiskInternal(card: RileyCard): boolean {
  return (
    card.state === 'proposed' &&
    card.audience === 'internal' &&
    card.can_confirm &&
    !(card.blocking_errors?.length ?? 0) &&
    LOW_RISK_KINDS.has(card.kind)
  )
}

const titleOf = (card: RileyCard) => card.title ?? card.kind.replaceAll('_', ' ')

/** One line of "what this does": the card's impact sentence, trimmed. */
function effectOf(card: RileyCard): string {
  const text = (card.impact_text ?? '').replace(/\s+/g, ' ').trim()
  return text.length > 140 ? `${text.slice(0, 137)}…` : text
}

/**
 * The compact strip at the top of a Riley reply that made more than one card: one row per card with its title, its
 * effect and Confirm / Review / Dismiss inline. Review opens the full card below; a card that needs a warning
 * acknowledged also falls back to Review rather than confirming blind.
 */
export function ActionStrip({
  cards,
  onConfirm,
  onDismiss,
  onUseInReplyBox,
  onReview,
}: {
  cards: RileyCard[]
  onConfirm: (card: RileyCard) => Promise<RileyCard>
  /** #746: a reply card opens in the reply box instead of sending from the strip. */
  onUseInReplyBox?: (card: RileyCard) => Promise<RileyCard>
  onDismiss: (card: RileyCard, reason?: string | null) => Promise<RileyCard>
  onReview: (card: RileyCard) => void
}) {
  const [busy, setBusy] = React.useState<ReadonlySet<string>>(new Set())
  const [notes, setNotes] = React.useState<Record<string, string>>({})
  const mark = (id: string, on: boolean) =>
    setBusy((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const run = async (card: RileyCard, action: 'confirm' | 'dismiss' | 'reply_box', reason?: string | null): Promise<boolean> => {
    mark(card.id, true)
    setNotes((current) => ({ ...current, [card.id]: '' }))
    try {
      await (action === 'confirm' ? onConfirm(card) : action === 'reply_box' ? onUseInReplyBox?.(card) : onDismiss(card, reason ?? null))
      return true
    } catch (reason) {
      if (action === 'confirm' && acknowledgementOf(reason)) {
        setNotes((current) => ({ ...current, [card.id]: 'Needs a closer look before it runs.' }))
        onReview(card)
      } else {
        setNotes((current) => ({
          ...current,
          [card.id]: reason instanceof Error ? reason.message : 'The card could not be updated.',
        }))
      }
      return false
    } finally {
      mark(card.id, false)
    }
  }

  const pending = cards.filter((card) => card.state === 'proposed')
  const allLowRisk = pending.length > 1 && pending.every(isLowRiskInternal)
  const [bulk, setBulk] = React.useState(false)
  const confirmAll = async () => {
    setBulk(true)
    try {
      for (const card of pending) {
        if (!(await run(card, 'confirm'))) break
      }
    } finally {
      setBulk(false)
    }
  }
  const heading = pending.length
    ? `${pending.length} action${pending.length === 1 ? '' : 's'} ready`
    : `${cards.length} actions · all decided`

  return (
    <div
      className="min-w-0 max-w-full rounded-lg border border-riley-border bg-riley-soft/40 px-3 py-2"
      data-slot="action-strip"
      data-pending={pending.length}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Zap aria-hidden className="size-3.5 shrink-0 text-riley" />
        <p className="min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide text-riley">{heading}</p>
        {allLowRisk ? (
          <Button
            size="sm"
            variant="primary"
            pending={bulk}
            onClick={() => void confirmAll()}
            title="Every card here is internal-only and low-risk: nothing is sent to a customer"
          >
            <Check /> Confirm all
          </Button>
        ) : null}
      </div>
      <ul className="mt-1.5 divide-y divide-border/60">
        {cards.map((card) => {
          const proposed = card.state === 'proposed'
          const working = busy.has(card.id)
          const canConfirm = card.can_confirm && !(card.blocking_errors?.length ?? 0)
          const canDismiss = card.can_dismiss ?? card.can_confirm
          return (
            <li key={card.id} className="min-w-0 py-1.5" data-strip-card={card.id}>
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <div className="min-w-0 flex-1 basis-40">
                  <p className="truncate text-sm font-medium" title={titleOf(card)}>
                    {titleOf(card)}
                  </p>
                  <p className="truncate text-xs text-muted-foreground" title={effectOf(card)}>
                    {effectOf(card)}
                  </p>
                </div>
                {proposed ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-1">
                    {canConfirm && isReplyCard(card) && onUseInReplyBox ? (
                      <Button size="sm" variant="primary" disabled={bulk} pending={working} onClick={() => void run(card, 'reply_box')}>
                        Use in reply box
                      </Button>
                    ) : canConfirm ? (
                      <Button size="sm" variant="primary" disabled={bulk} pending={working} onClick={() => void run(card, 'confirm')}>
                        Confirm
                      </Button>
                    ) : null}
                    <Button size="sm" disabled={working || bulk} onClick={() => onReview(card)}>
                      Review
                    </Button>
                    {canDismiss ? (
                      <DismissControl pending={working || bulk} onDismiss={(reason) => void run(card, 'dismiss', reason)} />
                    ) : null}
                  </div>
                ) : (
                  <Chip tone={card.state === 'failed' ? 'danger' : 'outline'} size="sm">
                    {card.state === 'dismissed'
                      ? 'Dismissed'
                      : card.state === 'failed'
                        ? 'Failed'
                        : card.state === 'expired'
                          ? 'Expired'
                          : 'Done'}
                  </Chip>
                )}
              </div>
              {notes[card.id] ? (
                <p role="alert" className="mt-0.5 text-xs text-destructive">
                  {notes[card.id]}
                </p>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
